import { type User, UserPlans } from '@pn/core/domain/user';
import { ConfigurationError } from '@pn/core/errors';
import { isNil } from 'lodash-es';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cordova: any;
  }
}

export function isProduction(): boolean {
  return getHostEnv() === 'production';
}

export function isStaging(): boolean {
  return getHostEnv() === 'staging';
}

export function isDevelopment(): boolean {
  return getHostEnv() === 'development';
}

function getHostEnv(): string {
  return (
    import.meta.env?.VITE_APP_HOST_ENV ??
    (typeof process !== 'undefined' ? process.env?.HOST_ENV : undefined) ??
    'production'
  );
}

export function isCordova(): boolean {
  return !isNil(window.cordova);
}

export function isFreeCordova(user: User | undefined): boolean {
  return (
    isCordova() && (isNil(user?.userPlan) || user?.userPlan === UserPlans.Free)
  );
}

/**
 * For local testing use http://us.localhost:3000
 */
export function isUS(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    window.location.hostname.includes('us-maps.stackdx.com') ||
    window.location.hostname.includes('us.localhost')
  );
}

export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export function isNode(): boolean {
  return typeof process !== 'undefined' && !!process.versions?.node;
}

export function getCurrentBuildNumber(): number {
  return Number(import.meta.env.VITE_APP_BUILD_NUMBER ?? 0);
}

/**
 * Environment variables.
 */

function set(key: string, value: string | undefined) {
  if (isNil(value)) {
    throw new ConfigurationError(`Environment variable ${key} is not set`);
  }
  env[key] = value;
}

const env: Record<string, string> = {};

set('PN_API_URL', import.meta.env.VITE_APP_API_URL ?? process.env.PN_API_URL);
set('PN_API_KEY', import.meta.env.VITE_APP_API_KEY ?? process.env.PN_API_KEY);
set(
  'PN_WS_URL',
  isProduction()
    ? 'wss://maps-api.stackdx.com'
    : 'wss://staging-maps-api.stackdx.com'
);
set(
  'PN_EXTRACTS_URL',
  isProduction()
    ? 'https://extracts.stackdx.com'
    : 'https://staging-extracts.stackdx.com'
);
set(
  'STACKDX_AI_LAMBDA_URL',
  isProduction()
    ? 'https://rntcu4w5nibkhfkzqo4zge5zk40bolgn.lambda-url.us-east-1.on.aws'
    : 'https://7ryifo5sbbuqpbsyigvfkcwtae0xehtv.lambda-url.us-east-1.on.aws'
);
set(
  'PN_MAPBOX_ACCESS_TOKEN',
  'pk.eyJ1Ijoic2JpbHN0b24iLCJhIjoiY2l1eGJmYXFkMDQ0bzJvbDF3cm43YW8yZiJ9.shjV2e-iaV6RcTjP_7GiMw'
);

export default env;
